import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '../config/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Get language from localStorage or default to English
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('terraMindLanguage') || 'el';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('terraMindLanguage', language);
    // The browser drops the accents of Greek capitals (CSS text-transform /
    // MUI overline) only when the page says it is Greek. With lang="en" the
    // section titles read «ΠΡΌΒΛΕΨΗ» (UI review 2026-10-01).
    document.documentElement.lang = language;
  }, [language]);

  // Translation function. Optional `vars` object substitutes {placeholders}
  // in the translated string. Returns the key when no translation is found
  // so missing keys are immediately visible in the UI.
  const t = (key, vars) => {
    const template = translations[language]?.[key] || translations.en[key] || key;
    if (!vars) return template;
    return template.replace(/{(\w+)}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : match,
    );
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'el' : 'en');
  };

  const value = {
    language,
    setLanguage,
    t,
    toggleLanguage,
    isGreek: language === 'el',
    isEnglish: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
